import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TwDocumentacionCamViewModel } from 'src/app/components/_mantenimiento/campanias/campania/documentacioncam/documentacioncam.types';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { NoWhiteSpaceValidator } from 'src/app/components/core/_infrastructure/classes/noWhiteSpaceValidator';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { DocumentModule, UploadedFileViewModel } from 'src/app/components/core/components/uploader/document_uploader.types';
import { DocumentoSolicitudCamViewModel, EditionMode, TwDocumentoSolicitudCamBase } from '../solicitud-documentacion.types';
import { DocumentosSolicitudCamService } from '../solicitud-documentacion.service';
import { catchError, of, tap } from 'rxjs';
import { ConfigurationService } from '../../../../core/_infrastructure/classes/services/configuration.service';

@Component({
    selector: 'app-solicitud-documentacion-edition',
    templateUrl: './solicitud-documentacion-edition.component.html',
    styleUrls: ['./solicitud-documentacion-edition.component.scss'],
})
export class SolicitudDocumentacionEditionComponent extends ComponentBase implements OnInit, OnChanges, OnDestroy {
    @HostListener('window:beforeunload', ['$event'])
    forceCheckUnload(): boolean {
        // Prevenimos la pérdida de datos, si el usuario recarga accidentalmente la página.
        // Lo controlamos y el navegador le requiere confirmación para finalizar la recarga.
        this.purgeFile();
        return false;
    }

    @Input() mode: EditionMode;
    @Input() readonly: boolean;
    @Input() doc: DocumentoSolicitudCamViewModel | null;
    @Input() idSolicitudCam: number;
    @Input() tiposDocumentacion: TwDocumentacionCamViewModel[];
    @Output() saved = new EventEmitter<boolean>();
    @Output() fileUpload = new EventEmitter<string>(); // En caso que el usuario no guardara la documentación vinculada al fichero adjuntado, el fichero tendrá que ser eliminado de la BD.
    @Output() filePurged = new EventEmitter<string>(); // En caso que el usuario no guardara la documentación vinculada al fichero adjuntado, el fichero tendrá que ser eliminado de la BD.

    isCollapsedCabecera = false;
    visibilitySpinnerGuardando = false;
    formGroup: FormGroup;
    documentModuleCampania = DocumentModule.Campania;
    uploadedFile: UploadedFileViewModel;
    payload: TwDocumentoSolicitudCamBase;
    private docSaved = false;
    editionMode = EditionMode;

    private successMessage = 'Documentación guardada correctamente';
    private fileSuccessMessage = 'Fichero subido correctamente';

    private filePendingToPurge = '';
    megaSizeLimit = 0;

    constructor(
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private documentosSolicitudCamService: DocumentosSolicitudCamService,
        protected configService: ConfigurationService,
    ) {
        super(authService, notificationService, exceptionHandler, configService);
    }

    ngOnInit(): void {
        this.initComponent();
        this.MegaSizeLimitsCargado$.subscribe(() => {
            this.megaSizeLimit = this.getValorSizeLimit('solicitud-documentacion-edition');
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.doc?.currentValue) {
            this.initComponent();
        }
        if (changes?.readonly?.currentValue) {
            this.disableControls(changes?.readonly?.currentValue);
        }
    }

    private initComponent(): void {
        this.buildForm();
        this.createPayload();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Descripcion: [{ value: null, disabled: this.readonly }, Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
            IdDocumentacionCam: [{ value: null, disabled: this.readonly }, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            Obligatoria: [{ value: null, disabled: true }], // no se enviará,
            NombreArchivo: [{ value: null, disabled: this.mode === EditionMode.Edition || this.readonly }, Validators.required],
        });
        this.form = this.formGroup;
    }

    private disableControls(readonly: boolean): void {
        if (readonly) {
            this.formGroup.controls['Descripcion'].disable();
            this.formGroup.controls['IdDocumentacionCam'].disable();
            this.formGroup.controls['NombreArchivo'].disable();
        }
    }

    private createPayload(): void {
        this.payload = new TwDocumentoSolicitudCamBase();
        this.uploadedFile = new UploadedFileViewModel();

        if (this.mode === EditionMode.Creation) {
            this.payload.Id = null;
            this.payload.IdSolicitudCam = this.idSolicitudCam;
            this.payload.FechaCreacion = null;
            this.payload.IdUsuario = null;
            this.payload.MarkToDelete = 0;
            // El resto de campos se rellenan cuando se vaya a guardar la documentación
        } else {
            // Edition mode
            if (!this.doc) {
                return;
            }
            this.payload.Id = this.doc.Id;
            this.payload.IdSolicitudCam = this.doc.IdSolicitudCam;
            this.payload.FechaCreacion = this.doc.FechaCreacion;
            this.payload.IdUsuario = this.doc.IdUsuario;
            this.payload.MarkToDelete = this.doc.MarkToDelete;
            this.payload.CodigoStorage = this.doc.CodigoStorage;
            this.payload.Descripcion = this.doc.Descripcion;
            this.payload.FileSize = this.doc.FileSize;
            this.payload.IdDocumentacionCam = this.doc.IdDocumentacionCam;
            this.payload.NombreArchivo = this.doc.NombreArchivo;

            this.uploadedFile.Guid = this.doc.CodigoStorage;
            this.uploadedFile.Name = this.doc.NombreArchivo;
            this.uploadedFile.Size = this.doc.FileSize;
        }

        this.updateForm();
    }

    private updateForm(): void {
        const tipoDoc = this.tiposDocumentacion.find(tipos => tipos.Id === this.payload.IdDocumentacionCam);
        if (!tipoDoc) {
            return;
        }
        this.formGroup.patchValue({
            Descripcion: this.payload.Descripcion,
            IdDocumentacionCam: this.payload.IdDocumentacionCam,
            Obligatoria: tipoDoc.Obligatoria,
            NombreArchivo: this.payload.NombreArchivo,
        });
        this.cdr.detectChanges();
    }

    onSelectTipo(tipo: TwDocumentacionCamViewModel): void {
        if (!tipo) {
            return;
        }
        const tipoDoc = this.tiposDocumentacion.find(tipos => tipos.Id === tipo.Id);
        if (!tipoDoc) {
            return;
        }
        this.formGroup.patchValue({
            Obligatoria: tipoDoc.Obligatoria,
        });
    }

    /**
     * Se recibe el emit desde el componente hijo, Uploader Local, del fichero cargado y ya grabado en BD
     * Indicamos al componente container, que el fichero ha sido adjuntado
     * @param uploadedFile UploadedFileViewModel
     */
    onFileUploaded(uploadedFile: UploadedFileViewModel): void {
        this.payload.CodigoStorage = uploadedFile.Guid;
        this.payload.NombreArchivo = uploadedFile.Name;
        this.payload.FileSize = uploadedFile.Size;

        this.fileUpload.emit(uploadedFile.Guid);
        // Lo marcamos pendiente de purgar. En caso que el usuario no guardara la documentación vinculada a éste fichero, el fichero tendrá que ser eliminado de la BD.

        this.purgeFile();
        this.filePendingToPurge = uploadedFile.Guid;
        this.docSaved = true;

        this.addSuccessMessage(this.fileSuccessMessage);
        //en Uploader Local obtenemos el valor para este campo obligatorio en subida local
        this.form.controls['NombreArchivo'].setValue(this.uploadedFile!.Name);
    }

    get ficheroSubido(): boolean {
        return this.uploadedFile.Guid !== undefined && this.uploadedFile.Guid !== null && this.uploadedFile.Guid !== '' && !this.uploadedFile.MarkToDelete;
    }

    onFileDeleted(deletedFile: UploadedFileViewModel) {
        this.uploadedFile = deletedFile;
    }

    private purgeFile(): void {
        if (this.readonly) {
            return;
        }
        if (this.filePendingToPurge !== '') {
            this.documentosSolicitudCamService.deleteFile(this.filePendingToPurge).subscribe();
            this.filePurged.emit(this.filePendingToPurge);
            this.filePendingToPurge = '';
        }
    }

    onClickSave(): void {
        this.payload.Descripcion = this.formGroup.value['Descripcion'];
        this.payload.IdDocumentacionCam = this.formGroup.value['IdDocumentacionCam'];
        this.documentosSolicitudCamService
            .persist(this.payload)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.formGroup.disable();
                    this.filePendingToPurge = '';
                    this.addSuccessMessage(this.successMessage);
                    this.saved.emit(true);
                    this.docSaved = true;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinnerGuardando = false;
                    this.formGroup?.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }

    get uploaderReadonly(): boolean {
        return this.mode === EditionMode.Edition || this.readonly || this.docSaved;
    }

    get docCanBeSaved(): boolean {
        return this.docSaved && this.formGroup.dirty && this.formGroup.valid;
    }

    private get archiveInvalid(): boolean {
        return (
            !this.formGroup.controls['NombreArchivo'].valid ||
            !this.formGroup.controls['NombreArchivo'].dirty ||
            this.formGroup.controls['NombreArchivo'].pristine
        );
    }

    esObligatoria(tipo: TwDocumentacionCamViewModel): boolean {
        if (!tipo) {
            return false;
        }
        const tipoDoc = this.tiposDocumentacion.find(tipos => tipos.Id === tipo.Id);
        if (!tipoDoc) {
            return false;
        }
        return tipoDoc.Obligatoria;
    }

    ngOnDestroy(): void {
        this.purgeFile();
    }
}
